// Client-side PDF page operations via pdf-lib. No backend, no upload — these
// run in the renderer on the open document's bytes and return new bytes the
// viewer re-renders (see documentStore.replaceBytes). All indices are 0-based.
import { PDFDocument, degrees } from "pdf-lib";
import type { PageRange } from "../../utils/pageRanges";

const load = (bytes: Uint8Array) =>
  PDFDocument.load(bytes, { ignoreEncryption: true });

/** Rotate the given pages (or all) by a delta in degrees, relative to current. */
export const rotatePages = async (
  bytes: Uint8Array,
  deltaDegrees: number,
  pageIndices?: number[],
): Promise<Uint8Array> => {
  const doc = await load(bytes);
  const pages = doc.getPages();
  const targets = pageIndices ?? pages.map((_, i) => i);
  for (const i of targets) {
    const page = pages[i];
    if (!page) continue;
    const current = page.getRotation().angle;
    const next = ((current + deltaDegrees) % 360 + 360) % 360;
    page.setRotation(degrees(next));
  }
  return doc.save();
};

/** New document containing only `indices`, in the order given (extract/reorder). */
export const extractPages = async (
  bytes: Uint8Array,
  indices: number[],
): Promise<Uint8Array> => {
  const src = await load(bytes);
  const out = await PDFDocument.create();
  const copied = await out.copyPages(src, indices);
  copied.forEach((p) => out.addPage(p));
  return out.save();
};

/** New document with `indices` removed (keeps original order of the rest). */
export const deletePages = async (
  bytes: Uint8Array,
  indices: number[],
): Promise<Uint8Array> => {
  const src = await load(bytes);
  const remove = new Set(indices);
  const keep = src.getPages().map((_, i) => i).filter((i) => !remove.has(i));
  return extractPages(bytes, keep);
};

/** Reorder to exactly `order` (a permutation of all page indices). */
export const reorderPages = (bytes: Uint8Array, order: number[]) =>
  extractPages(bytes, order);

/**
 * Merge `additions` into `baseBytes`, in order. The added pages are inserted
 * before base page `position` (0-based; 0 = start, base page count = end).
 * Defaults to the end, which keeps the original append behavior.
 */
export const mergePdfs = async (
  baseBytes: Uint8Array,
  additions: Uint8Array[],
  position?: number,
): Promise<Uint8Array> => {
  const base = await load(baseBytes);
  const baseCount = base.getPageCount();
  for (const addBytes of additions) {
    const add = await load(addBytes);
    const copied = await base.copyPages(add, add.getPageIndices());
    copied.forEach((p) => base.addPage(p));
  }
  const at = Math.min(Math.max(position ?? baseCount, 0), baseCount);
  if (at === baseCount) return base.save();

  const total = base.getPageCount();
  const addedCount = total - baseCount;
  const order = [
    ...Array.from({ length: at }, (_, i) => i),
    ...Array.from({ length: addedCount }, (_, i) => baseCount + i),
    ...Array.from({ length: baseCount - at }, (_, i) => at + i),
  ];
  return reorderPages(await base.save(), order);
};

export interface SplitPart {
  name: string;
  bytes: Uint8Array;
  pageStart: number; // 1-based, inclusive
  pageEnd: number; // 1-based, inclusive
}

/** Split into fixed-size chunks of `pagesPerChunk` pages. */
export const splitIntoChunks = async (
  bytes: Uint8Array,
  baseName: string,
  pagesPerChunk: number,
): Promise<SplitPart[]> => {
  const src = await load(bytes);
  const total = src.getPageCount();
  const parts: SplitPart[] = [];
  for (let start = 0; start < total; start += pagesPerChunk) {
    const end = Math.min(start + pagesPerChunk, total);
    const indices = Array.from({ length: end - start }, (_, i) => start + i);
    const chunkBytes = await extractPages(bytes, indices);
    parts.push({
      name: `${baseName}_p${start + 1}-${end}.pdf`,
      bytes: chunkBytes,
      pageStart: start + 1,
      pageEnd: end,
    });
  }
  return parts;
};

/** Split into one file per 1-based inclusive page range (e.g. 1–2, 5). */
export const splitByRanges = async (
  bytes: Uint8Array,
  baseName: string,
  ranges: PageRange[],
): Promise<SplitPart[]> => {
  const parts: SplitPart[] = [];
  for (const { start, end } of ranges) {
    const indices = Array.from({ length: end - start + 1 }, (_, i) => start - 1 + i);
    const chunkBytes = await extractPages(bytes, indices);
    const label = start === end ? `${start}` : `${start}-${end}`;
    parts.push({
      name: `${baseName}_pages${label}.pdf`,
      bytes: chunkBytes,
      pageStart: start,
      pageEnd: end,
    });
  }
  return parts;
};

export const getPageCount = async (bytes: Uint8Array): Promise<number> =>
  (await load(bytes)).getPageCount();
